import "./JoinSpaceConfirmation.scss";
import classNames from "classnames";
import React, { useState } from "react";
import { GuestModeDisabledDetails } from "@highnote/server/src/core/shared-util";
import { EditableArtwork } from "App/components/EditableArtwork";
import { BUTTON_THEME, Button } from "App/core/Button";
import { useToast } from "App/common/useToast";
import { useAuth } from "App/components/Auth";
import { AUTH_TYPE, AuthForm } from "App/components/Auth/AuthForm";
import { JOIN_ENTITY_TRIGGER } from "App/components/Auth/util";
import { Byline } from "App/routes/Main/Discover/tiles/Byline";
import { useCountdown } from "App/common/useCountdown";
import { SpaceCountdownPanel } from "./SpaceCountdownPanel";

const DEFAULT_ARTWORK = "/public/default-space-artwork.png";

export const JoinSpaceConfirmation = ({
  spaceName,
  spaceArtworkUrl,
  referrerId,
  onSubmit,
  guestModePreview,
}: {
  spaceName: string;
  spaceArtworkUrl?: string;
  referrerId?: string;
  onSubmit: () => Promise<void>;
  guestModePreview?: GuestModeDisabledDetails;
}) => {
  const { user } = useAuth();
  const { addErrorMessage } = useToast();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [step, setStep] = useState<"preview" | "login">("preview");

  const preview = guestModePreview;
  const previewName = preview?.spaceName ?? spaceName;
  const previewArtworkUrl = preview?.spaceArtworkUrl ?? spaceArtworkUrl;
  const description = preview?.description;
  const creatorName = preview?.creatorName;
  const creatorAvatarUrl = preview?.creatorAvatarUrl;
  const memberCount = preview?.memberCount;
  const memberAvatarUrls = preview?.memberAvatarUrls;
  const countdown = useCountdown(preview?.countdownFrom);

  const handleJoinSpace = async () => {
    setIsSubmitting(true);
    try {
      await onSubmit();
    } catch (err) {
      addErrorMessage(err.message);
      setIsSubmitting(false);
    }
  };

  /**
   * Preview CTA: a logged-in user joins straight away; a logged-out user
   * advances to the login step to authenticate first.
   */
  const handleJoinClick = () => {
    if (user) {
      handleJoinSpace();
    } else {
      setStep("login");
    }
  };

  /**
   * The countdown/facepile + Join button lockup. Rendered in two spots (CSS
   * can't reparent): "inline" tucks it into the info column on desktop, "docked"
   * pins it to the bottom of the view on mobile — each hidden at the other
   * breakpoint.
   */
  const ctaPane = (variant: "inline" | "docked") => (
    <SpaceCountdownPanel
      className={classNames("join-space-cta", `join-space-cta--${variant}`)}
      countdown={countdown}
      members={memberAvatarUrls?.map((avatarURL) => ({ avatarURL }))}
      memberCount={memberCount ?? undefined}
      joined={false}
      // This landing is only reachable through an active share link, so the open
      // status is always LIVE.
      isLinkShared
      // Always the web "Join Space" CTA here (joined={false}), including below M:
      // this gate blocks all viewing until the user joins, so it must keep the
      // join flow (sign up → join) on web instead of pushing to the app.
      allowJoinOnMobileWeb
      onJoin={handleJoinClick}
      isJoining={isSubmitting}
    />
  );

  if (step === "preview") {
    return (
      <div className="join-space-confirmation">
        <div className="join-space-preview">
          <div className="join-space-preview-top">
            <div className="join-space-preview-artwork">
              <EditableArtwork
                disabled
                artworkUrl={previewArtworkUrl ?? ""}
                fallbackArtworkUrl={DEFAULT_ARTWORK}
                blurredBackground="large"
              />
            </div>
            <div className="join-space-preview-info">
              <div className="join-space-preview-details">
                <h2 className="join-space-preview-title">{previewName}</h2>
                {creatorName ? (
                  <Byline picture={creatorAvatarUrl} name={creatorName} />
                ) : null}
                {description ? (
                  <p className="join-space-preview-description">
                    {description}
                  </p>
                ) : null}
              </div>
              {/* Desktop: tucked into the info column. */}
              {ctaPane("inline")}
            </div>
          </div>

          {/* Mobile: docked to the bottom of the view. */}
          {ctaPane("docked")}
        </div>
      </div>
    );
  }

  // Login step — only reached by logged-out users clicking "Join Space".
  return (
    <div className="join-space-confirmation">
      <div className="auth-wrapper">
        <div className="join-confirmation-auth">
          <div className="join-confirmation-auth-artwork">
            <EditableArtwork
              disabled
              artworkUrl={previewArtworkUrl ?? ""}
              fallbackArtworkUrl={DEFAULT_ARTWORK}
              blurredBackground="large"
            />
          </div>
          {user ? (
            <div className="join-confirmation-auth-section">
              <p className="join-confirmation-auth-section-header">
                Join {spaceName}
              </p>
              <p className="join-confirmation-auth-section-description">
                Joining this Space shares your username and activity with other
                members.
              </p>
              <Button
                disabled={isSubmitting}
                loading={isSubmitting}
                theme={BUTTON_THEME.CTA}
                onClick={handleJoinSpace}
              >
                Join Space
              </Button>
            </div>
          ) : (
            <div className="join-confirmation-auth-section">
              <p className="join-confirmation-auth-section-header">
                Join {spaceName}
              </p>
              <AuthForm
                type={AUTH_TYPE.JOIN}
                returnTo={`${location.pathname}${location.search}`}
                trigger={JOIN_ENTITY_TRIGGER.BUTTON}
                hideWelcomeSurvey
                signUpMetadata={{
                  referrer_type: "invite_link_join_button",
                  referrer_id: referrerId,
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
